import { useEffect, useRef } from "react";
import * as THREE from "three";
import { logger } from "../../utils/logger";
import {
  configureRaycasterForScenePicking,
  preferTendonConstraintMarkerHit,
} from "../scene/scenePicking";
import { createCirclePickHoverState } from "./useScenePicker/circlePickInteraction";
import { ensurePosPickMarker } from "./useScenePicker/posPickInteraction";
import { handleScenePickerClick } from "./useScenePicker/scenePickerClick";
import { CLICK_VS_DRAG_PX } from "./useScenePicker/scenePickerConstants";
import { processScenePickerPointerMove } from "./useScenePicker/scenePickerHover";
import { createSceneRmbDocumentTracking } from "./useScenePicker/sceneRmbDocumentTracking";

export function useScenePicker({
  rendererRef,
  sceneRef,
  sceneObjectsRef,
  cameraRef,
  constraintMarkersGroupRef,
  constraintMarkerMeshesRef,
  constraintHoveredKeyRef,
  sceneRmbContextAnchorRef,
  sceneRmbContextDraggedRef,
  setHoverPreviewBody,
  setHoverPreviewJoint,
  setHoverPreviewMesh,
  isOrbitingRef,
  preferPointerAfterOrbitRef,
  showVisual,
  showJoints,
  showCollision,
  showInertials,
  showSites,
  showConstraintMarkers,
  selectionActions,
  getCollisionTarget,
  getVisualGeomTarget,
  findValidTarget,
  getResolvedFocusBodiesGateSet,
  applyVisualMeshMultiSelection,
  applyInspectCandidateSelection,
  getContextTargetSnapshot,
  handleSceneContextMenuCapture,
  positionJointSliderNextToJoint,
  showJointPositionSlider,
  showSavedCameraMarkers,
  showScaleGizmo,
  isCollisionGeomObject,
  getOwningBodyUuidForObject,
  getJointIdFromObject,
}) {
  const pickerInputsRef = useRef(null);
  pickerInputsRef.current = {
    rendererRef,
    sceneRef,
    sceneObjectsRef,
    cameraRef,
    constraintMarkersGroupRef,
    constraintMarkerMeshesRef,
    constraintHoveredKeyRef,
    sceneRmbContextAnchorRef,
    sceneRmbContextDraggedRef,
    setHoverPreviewBody,
    setHoverPreviewJoint,
    setHoverPreviewMesh,
    isOrbitingRef,
    preferPointerAfterOrbitRef,
    selectionActions,
    getCollisionTarget,
    getVisualGeomTarget,
    findValidTarget,
    getResolvedFocusBodiesGateSet,
    applyVisualMeshMultiSelection,
    applyInspectCandidateSelection,
    getContextTargetSnapshot,
    handleSceneContextMenuCapture,
    positionJointSliderNextToJoint,
    isCollisionGeomObject,
    getOwningBodyUuidForObject,
    getJointIdFromObject,
  };

  useEffect(() => {
    const inputs = pickerInputsRef.current;
    const {
      rendererRef,
      sceneRef,
      sceneObjectsRef,
      cameraRef,
      constraintMarkersGroupRef,
      constraintMarkerMeshesRef,
      constraintHoveredKeyRef,
      sceneRmbContextAnchorRef,
      sceneRmbContextDraggedRef,
      setHoverPreviewBody,
      setHoverPreviewJoint,
      setHoverPreviewMesh,
      isOrbitingRef,
      preferPointerAfterOrbitRef,
      selectionActions,
      getCollisionTarget,
      getVisualGeomTarget,
      findValidTarget,
      getResolvedFocusBodiesGateSet,
      applyVisualMeshMultiSelection,
      applyInspectCandidateSelection,
      getContextTargetSnapshot,
      handleSceneContextMenuCapture,
      positionJointSliderNextToJoint,
      isCollisionGeomObject,
      getOwningBodyUuidForObject,
      getJointIdFromObject,
    } = inputs;

    if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return;
    const canvas = rendererRef.current.domElement;
    const raycaster = new THREE.Raycaster();
    configureRaycasterForScenePicking(raycaster);
    const pointer = new THREE.Vector2();
    const circleHover = createCirclePickHoverState();
    const rmbDocumentTracking = createSceneRmbDocumentTracking({
      canvas,
      sceneRmbContextAnchorRef,
      sceneRmbContextDraggedRef,
    });

    const pickConstraintMarkerKey = () => {
      if (
        !constraintMarkersGroupRef.current?.visible ||
        constraintMarkerMeshesRef.current.length === 0
      ) {
        return null;
      }
      const markerHits = raycaster.intersectObjects(constraintMarkerMeshesRef.current, false);
      const markerPick = preferTendonConstraintMarkerHit(markerHits);
      const markerKey = markerPick?.object?.userData?.key;
      return markerKey == null || markerKey === "" ? null : String(markerKey);
    };

    const getSharedHandlerArgs = () => ({
      canvas,
      camera: cameraRef.current,
      constraintHoveredKeyRef,
      findValidTarget,
      getCollisionTarget,
      getJointIdFromObject,
      getOwningBodyUuidForObject,
      getResolvedFocusBodiesGateSet,
      getVisualGeomTarget,
      isCollisionGeomObject,
      pickConstraintMarkerKey,
      pointer,
      raycaster,
      sceneObjectsRef,
      sceneRef,
      setHoverPreviewBody,
      setHoverPreviewJoint,
      setHoverPreviewMesh,
      showCollision,
      showJoints,
      showSavedCameraMarkers,
      showScaleGizmo,
      showVisual,
    });

    let pointerDownPos = null;
    let pointerMoveRafId = null;
    let pendingPointerMove = null;

    function onPointerDown(event) {
      if (window.isModelDragging) return;

      if (event.button === 2) {
        sceneRmbContextAnchorRef.current = { x: event.clientX, y: event.clientY };
        sceneRmbContextDraggedRef.current = false;
        rmbDocumentTracking.attach();
      }
      pointerDownPos = { x: event.clientX, y: event.clientY };
    }

    async function onPointerUp(event) {
      if (window.isModelDragging) {
        pointerDownPos = null;
        return;
      }

      if (event.button === 2 && !window.isRadialClickModeActive) {
        pointerDownPos = null;
        return;
      }

      const isClick =
        pointerDownPos &&
        Math.abs(event.clientX - pointerDownPos.x) < CLICK_VS_DRAG_PX &&
        Math.abs(event.clientY - pointerDownPos.y) < CLICK_VS_DRAG_PX;

      if (isClick) {
        handleScenePickerClick({
          ...getSharedHandlerArgs(),
          applyInspectCandidateSelection,
          applyVisualMeshMultiSelection,
          ensurePosPickMarker: () => ensurePosPickMarker(sceneRef.current),
          event,
          getContextTargetSnapshot,
          positionJointSliderNextToJoint,
          preferPointerAfterOrbitRef,
          restoreLastCircleHover: circleHover.restore,
          selectionActions,
          showConstraintMarkers,
          showInertials,
          showJointPositionSlider,
          showSites,
        });
      }
      pointerDownPos = null;
    }

    function processPointerMove(clientX, clientY, buttons = 0) {
      processScenePickerPointerMove({
        ...getSharedHandlerArgs(),
        applyCircleHover: circleHover.applyHover,
        buttons,
        clearCircleHoverIfActive: circleHover.clearIfActive,
        clientX,
        clientY,
        isOrbitingRef,
      });
    }

    function onPointerMove(event) {
      pendingPointerMove = {
        clientX: event.clientX,
        clientY: event.clientY,
        buttons: event.buttons || 0,
      };
      if (pointerMoveRafId !== null) return;
      pointerMoveRafId = requestAnimationFrame(() => {
        pointerMoveRafId = null;
        const nextPointerMove = pendingPointerMove;
        pendingPointerMove = null;
        if (!nextPointerMove) return;
        processPointerMove(
          nextPointerMove.clientX,
          nextPointerMove.clientY,
          nextPointerMove.buttons
        );
      });
    }

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("contextmenu", handleSceneContextMenuCapture, true);

    return () => {
      rmbDocumentTracking.detach();
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("contextmenu", handleSceneContextMenuCapture, true);
      if (pointerMoveRafId !== null) {
        cancelAnimationFrame(pointerMoveRafId);
        pointerMoveRafId = null;
      }
      pendingPointerMove = null;
      try {
        circleHover.restore();
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
    };
  }, [
    showVisual,
    showJoints,
    showCollision,
    showSites,
    showInertials,
    showScaleGizmo,
    showJointPositionSlider,
    showSavedCameraMarkers,
    showConstraintMarkers,
  ]);
}
